// ======================================================================
// 📁 File: BusinessPartnerFormDrawer.jsx
// 📝 Purpose:
//   Business Partner Master – Create / Edit Drawer
//   - Uses enterprise reusable ERPSideDrawer
//   - Modular tabs
//   - Address / Contact / Tax / Bank supported
//
// FIX LOG:
// 2026-01-31 09:45 IST
// - Added frontend payload normalization before API call
// - Maps UI fields → backend-required fields
// - Fixes "Missing required field(s): bp_name" error
//
// 2026-01-31 14:30 IST
// - ✅ FIXED: edit drawer empty issue
// - ✅ Load backend data on edit open
// - ✅ No existing code removed
// ======================================================================

import React, { useState, useEffect } from "react";

import businessPartnerService from "../services/businessPartnerService";

import ERPSideDrawer from "../components/common/ERPSideDrawer";

import useBusinessPartnerForm from "./hooks/useBusinessPartnerForm";

import BasicInfoTab from "./tabs/BasicInfoTab";
import RolesTab from "./tabs/RolesTab";

// OLD TABS (kept commented for reference)
// import AddressTab from "./tabs/AddressTab";
// import TaxTab from "./tabs/TaxTab";
// import BankTab from "./tabs/BankTab";

// NEW MODULAR TABS
import AddressTab from "../components/address/AddressTab";
import ContactTab from "../components/contact/ContactTab";
import TaxTab from "../components/tax/TaxTab";
import BankTab from "../components/bank/BankTab";

export default function BusinessPartnerFormDrawer({
  open,
  mode,
  record,
  onClose,
  onSaved,
}) {
  const [activeTab, setActiveTab] = useState("BASIC");

  const {
    form,
    setForm,

    // Address
    addAddress,
    updateAddress,
    removeAddress,
    setDefaultAddress,

    // Contact
    addContact,
    updateContact,
    removeContact,

    // Tax
    addTax,
    updateTax,
    removeTax,

    // Bank
    addBank,
    updateBank,
    removeBank,
    setDefaultBank,
  } = useBusinessPartnerForm(record, open);

  // ------------------------------------------------------------------
  // ✅ FIX: Load backend data when editing
  // ------------------------------------------------------------------
  useEffect(() => {
    if (open && mode === "edit" && record?.id) {
      (async () => {
        try {
          const data =
            await businessPartnerService.getBusinessPartner(
              record.id
            );

          setForm(data);
        } catch (err) {
          console.error(
            "Failed to load Business Partner for edit",
            err
          );
        }
      })();
    }
  }, [open, mode, record?.id]);

  // ------------------------------------------------------------------
  // FIX: Frontend → Backend payload normalization
  // Added: 2026-01-31 09:45 IST
  // ------------------------------------------------------------------
  const buildPayload = (form) => {
  return {
    bp_name: form.bp_name || form.name,

    bp_type:
      form.bp_type ||
      form.partner_type ||
      "ORGANIZATION",

    roles: form.roles || [],

    addresses: form.addresses || [],

    // 🔥 FIX CONTACT SAVE ISSUE
    contacts: (form.contacts || []).map((c) => ({
      contact_name: c.contact_name || c.name,
      designation: c.designation,
      department: c.department,
      mobile: c.mobile,
      email: c.email,
      is_default: c.is_default || false,
      is_active: c.is_active !== false,
    })),

    taxes: form.taxes || [],

    banks: (form.banks || []).map((b) => ({
      bank_name: b.bank_name,
      account_no: b.account_number,
      ifsc: b.ifsc,
      branch: b.branch,
      is_default: b.is_default || false,
    })),
  };
};


  // ------------------------------------------------------------------
  // Drawer Tabs
  // ------------------------------------------------------------------
  const tabs = [
    { key: "BASIC", label: "Basic" },
    { key: "ROLES", label: "Roles" },
    { key: "ADDRESS", label: "Address" },
    { key: "CONTACT", label: "Contact" },
    { key: "TAX", label: "Tax" },
    { key: "BANK", label: "Bank" },
  ];

  return (
    <ERPSideDrawer
      open={open}
      title={
        mode === "edit"
          ? "Edit Business Partner"
          : "Create Business Partner"
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      onClose={onClose}
      footer={
        <>
          <button
            className="btn btn-secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={async () => {
              try {
                const payload = buildPayload(form);

                if (mode === "edit") {
                  await businessPartnerService.updateBusinessPartner(
                    record.id,
                    payload
                  );
                } else {
                  await businessPartnerService.createBusinessPartner(
                    payload
                  );
                }

                onSaved();
                onClose();
              } catch (err) {
                console.error("Save failed", err);
                alert(
                  err?.response?.data?.message ||
                    "Save failed. Please check console."
                );
              }
            }}
          >
            Save
          </button>

          
        </>
      }
    >
      {/* ================= BASIC ================= */}
      {activeTab === "BASIC" && (
        <BasicInfoTab
          form={form}
          setForm={setForm}
        />
      )}

      {/* ================= ROLES ================= */}
      {activeTab === "ROLES" && (
        <RolesTab
          form={form}
          setForm={setForm}
        />
      )}

      {/* ================= ADDRESS ================= */}
      {activeTab === "ADDRESS" && (
        <AddressTab
          addresses={form.addresses}
          onAdd={addAddress}
          onUpdate={updateAddress}
          onRemove={removeAddress}
          onSetDefault={setDefaultAddress}
        />
      )}

      {/* ================= CONTACT ================= */}
      {activeTab === "CONTACT" && (
        <ContactTab
          contacts={form.contacts}
          onAdd={addContact}
          onUpdate={updateContact}
          onRemove={removeContact}
        />
      )}

      {/* ================= TAX ================= */}
      {activeTab === "TAX" && (
        <TaxTab
          taxes={form.taxes}
          onAdd={addTax}
          onUpdate={updateTax}
          onRemove={removeTax}
        />
      )}

      {/* ================= BANK ================= */}
      {activeTab === "BANK" && (
        <BankTab
          banks={form.banks}
          onAdd={addBank}
          onUpdate={updateBank}
          onRemove={removeBank}
          onSetDefault={setDefaultBank}
        />
      )}
    </ERPSideDrawer>
  );
}
